import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { accordionConfig } from '@components/Accordion/AccordionConfig';
import AccordionTsoPq, { AccordionProps } from '@components/Accordion/AccordionTsoPq';
import BreadcrumbsTsoPq from '@components/Breadcrumbs/BreadcrumbsTsoPq';
import { DefaultPageLayoutTsoPq } from '@components/Grid/GridTsoPq';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { RouteMap } from '@constants/routes';
import { useGetAggregationIntervalGetAllQuery } from '@redux/apiSlices/aggregationIntervalApi';
import {
  ApiCommonExternalDtoImportOperationExternalDto,
  useGetImportOperationByIdQuery,
  useLazyPostImportOperationRetryImportQuantileQuery,
  usePostImportOperationRetryImportPlausibilityCheckMutation,
  usePostImportOperationRetryImportValidationMutation,
  usePutImportOperationMutation,
} from '@redux/apiSlices/importOperationApi';
import { useGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppSelector } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { setSelectedImportOperationId } from '@redux/slices/uploadedDataSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { findDeviceAndSubstation } from '@utils/deviceHierarchy';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import AccordionContent from './DetailView/AccordionContent';
import {
  infoContentMaps,
  quantileCalculationContent,
  statusChipOptions,
  statusMap,
  StatusProps,
  validationCases,
  validationErrorMap,
} from './DetailView/DetailViewConfig';
import PausibilityCheckDataGridTsoPq from './DetailView/PlausibilityCheckDetails';
import ValidityCheckDetails from './DetailView/ValidityCheckDetails';
import {
  StyledChipFilterTsoPq,
  StyledDetailColumn,
  StyledDetailColumnsContainer,
  StyledDetailsSection,
  StyledImportDetailsContainer,
  StyledUploadDetailsPageHeaderWrapper,
  StyledUploadDetailsWrapper,
  StyledUploadTitleContainer,
} from './StyledUploadDataComponents';

type Row = {
  id: any;
  start: string | number | Date | dayjs.Dayjs | null | undefined;
  end: string | number | Date | dayjs.Dayjs | null | undefined;
  rule: any;
  status: number;
  isPlausible: boolean;
};

const DetailedViewUploadedFile = () => {
  const [statuses, setStatuses] = useState<{ [key: string]: StatusProps }>();
  const [validationErrorReason, setValidationErrorReason] = useState<string | undefined>();
  const [errorDescription, setErrorDescription] = useState<{ [key: string]: string | undefined }>();
  const [importOperationDetails, setImportOperationDetails] =
    useState<ApiCommonExternalDtoImportOperationExternalDto>();

  const { importoperationid: importOperationId } = useParams();
  const { data: importOperationByIdQuery, isLoading: importOperationIsLoading } = useGetImportOperationByIdQuery(
    Number(importOperationId),
    {
      pollingInterval: 10000,
    }
  );
  const [triggerQuantileRetry] = useLazyPostImportOperationRetryImportQuantileQuery();
  const [triggerPlausabilityRetry] = usePostImportOperationRetryImportPlausibilityCheckMutation();
  const [triggerRetryImport] = usePostImportOperationRetryImportValidationMutation();
  const { data: aggregationIntervals } = useGetAggregationIntervalGetAllQuery();
  const { data: tsos } = useGetTsoGetAllQuery();
  const { data: deviceHierarchyData } = useGetSubstationGetSubstationDeviceHierarchyQuery(
    tsos?.externalDtos?.find((t) => t.isLoggedInUsersTso)?.id ?? skipToken
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const breadcrumbs = [
    { id: '1', label: t('upload.detailedView.breadcrumb1'), url: RouteMap.UPLOAD_ROUTE },
    { id: '2', label: t('upload.detailedView.breadcrumb2') },
  ];
  const selectedImportOperationId = useAppSelector((state) => state.uploadedData.selectedImportOperationId);
  const deviceId = importOperationDetails?.measurementDeviceId ?? -1;
  const hierarchy = deviceHierarchyData?.externalDtos ?? [];
  const hierarchyInfo = findDeviceAndSubstation(hierarchy, deviceId);
  const deviceLabel = hierarchyInfo?.deviceLabel;
  const deviceSubstation = hierarchyInfo?.substationLabel;
  const measurementPoint = hierarchyInfo?.measurementPointLabel;
  const plausibleStatusCode = 3;

  const [updateImportOperation] = usePutImportOperationMutation();

  const navigate = useNavigate();

  const onSubmitHandler = async (measurementDeviceId: number | null, aggregationIntervalId: number | null) => {
    if (!importOperationDetails?.id || !measurementDeviceId || !aggregationIntervalId || aggregationIntervalId < 0)
      return;

    const hasMeasurementDeviceChanged = measurementDeviceId !== importOperationDetails.measurementDeviceId;
    const hasAggregationIntervalChanged = aggregationIntervalId !== importOperationDetails.aggregationIntervalId;

    if (!hasMeasurementDeviceChanged && !hasAggregationIntervalChanged) {
      return;
    }

    try {
      await updateImportOperation({
        externalDto: {
          id: importOperationDetails.id,
          tsoId: importOperationDetails.tsoId,
          measurementDeviceId: measurementDeviceId,
          aggregationIntervalId: aggregationIntervalId,
        },
      }).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.validity.missing.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
      navigate(RouteMap.UPLOAD_ROUTE);
    } catch (error: any) {
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.validity.missing.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const getTranslatedAggregationName = (id: number | undefined | null): string | undefined => {
    const name = aggregationIntervals?.externalDtos?.find((item) => item.id === id)?.name;
    if (!name) return undefined;
    return name.startsWith('aggregationinterval.') ? t(`upload.${name}`) : name;
  };

  const formatDate = (date: string | undefined | null) => {
    return date ? format(new Date(date), 'dd.MM.yyyy HH:mm') : 'n.a.';
  };

  const handleRestartQuantileCalculation = async () => {
    if (!selectedImportOperationId) return;

    try {
      await triggerQuantileRetry(selectedImportOperationId).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.quantileCalc.tryAgain.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error('Error restarting quantile calculation:', error);
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.quantileCalc.tryAgain.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const handleRestartPlausabilityCheck = async () => {
    if (!selectedImportOperationId) return;

    try {
      await triggerPlausabilityRetry(selectedImportOperationId).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.plausibilityChanges.tryAgain.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error('Error restarting plausibility check:', error);
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.plausibilityChanges.tryAgain.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const handleRestartDataImport = async () => {
    if (!selectedImportOperationId) return;

    try {
      await triggerRetryImport(selectedImportOperationId).unwrap();
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.import.tryAgain.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );
    } catch (error: any) {
      console.error('Error restarting import:', error);
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.import.tryAgain.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const getContentForPlausability = () => {
    if (statuses?.plausibility === 'warning') {
      return <PausibilityCheckDataGridTsoPq />;
    }
    if (statuses?.plausibility === 'error') {
      return AccordionContent({
        title: t('upload.detailedView.plausibilityCheck.error.title'),
        content: t('upload.detailedView.plausibilityCheck.status.plausible.error'),
        label: t('upload.detailedView.plausibilityCheck.button.retry'),
        handler: () => handleRestartPlausabilityCheck(),
      });
    }

    return null;
  };

  const pageSummaryColumns = [
    {
      title: t('upload.detailedView.substation'),
      value: deviceSubstation ?? 'n.a.',
    },
    {
      title: t('upload.detailedView.measurementDevice'),
      value: measurementPoint && deviceLabel ? `${measurementPoint} - ${deviceLabel}` : 'n.a.',
    },
    {
      title: t('upload.detailedView.totalPeriod'),
      value: `${formatDate(importOperationDetails?.from)} - ${formatDate(importOperationDetails?.to)}`,
    },
    {
      title: t('upload.detailedView.aggregationInterval'),
      value: getTranslatedAggregationName(importOperationDetails?.aggregationIntervalId) ?? 'n.a.',
    },
  ];
  const selectedValidationCase = useMemo(() => {
    if (!statuses?.validation || !validationErrorReason) {
      return undefined;
    }

    const rawValidationCase =
      validationCases[statuses.validation as keyof typeof validationCases]?.[
        validationErrorReason as keyof (typeof validationCases)[keyof typeof validationCases]
      ];

    if (!rawValidationCase || !('title' in rawValidationCase)) {
      return undefined;
    }

    return {
      title: rawValidationCase.title,
      content: 'content' in rawValidationCase ? rawValidationCase.content : '',
      id: 'id' in rawValidationCase ? rawValidationCase.id : undefined,
    };
  }, [statuses?.validation, validationErrorReason]);

  const detailSectionMap: AccordionProps[] = useMemo(
    () => [
      {
        title: t('upload.detailedView.validityCheck'),
        titleSlot: (
          <StyledChipFilterTsoPq
            options={statusChipOptions[statuses?.validation ?? 'default'].style}
            textContent={statusChipOptions[statuses?.validation ?? 'default'].label}
          />
        ),
        infoIcon: accordionConfig.icon[statuses?.validation as keyof typeof accordionConfig.icon],
        infoContent: infoContentMaps.validation[statuses?.validation as keyof typeof infoContentMaps.validation],
        infoIconColor: accordionConfig.color[statuses?.validation ?? 'default'],
        content: (statuses?.validation === 'error' || statuses?.validation === 'warning') && (
          <ValidityCheckDetails
            errorReason={selectedValidationCase}
            validationErrorDetails={errorDescription?.validationErrorDetails}
            selectedMeasurementDeviceId={importOperationByIdQuery?.externalDto?.measurementDeviceId ?? null}
            selectedAggregationIntervalId={importOperationByIdQuery?.externalDto?.aggregationIntervalId ?? null}
            onSubmitHandler={onSubmitHandler}
          />
        ),
      },
      {
        title: t('upload.detailedView.importProcess'),
        titleSlot: (
          <StyledChipFilterTsoPq
            options={statusChipOptions[statuses?.import ?? 'default'].style}
            textContent={statusChipOptions[statuses?.import ?? 'default'].label}
          />
        ),
        infoIcon: accordionConfig.icon[statuses?.import as keyof typeof accordionConfig.icon],
        infoContent: infoContentMaps.import[statuses?.import as keyof typeof infoContentMaps.import],
        infoIconColor: accordionConfig.color[statuses?.import ?? 'default'],
        content:
          statuses?.import === 'error' &&
          AccordionContent({
            title: t('upload.detailedView.import.error.title'),
            content: t('upload.detailedView.import.error.content'),
            label: t('upload.detailedView.plausibilityCheck.button.retry'),
            handler: () => handleRestartDataImport(),
          }),
      },
      {
        title: t('upload.detailedView.plausibilityCheck'),
        titleSlot: (
          <StyledChipFilterTsoPq
            options={statusChipOptions[statuses?.plausibility ?? 'default'].style}
            textContent={statusChipOptions[statuses?.plausibility ?? 'default'].label}
          />
        ),
        infoIcon: accordionConfig.icon[statuses?.plausibility as keyof typeof accordionConfig.icon],
        infoContent: infoContentMaps.plausibility[statuses?.plausibility as keyof typeof infoContentMaps.plausibility],
        infoIconColor: accordionConfig.color[statuses?.plausibility ?? 'default'],
        content: getContentForPlausability(),
      },
      {
        title: t('upload.detailedView.accordion.infoContent.quantileCalculationContent'),
        titleSlot: (
          <StyledChipFilterTsoPq
            options={statusChipOptions[statuses?.quantileCalculation ?? 'default'].style}
            textContent={statusChipOptions[statuses?.quantileCalculation ?? 'default'].label}
          />
        ),
        infoIcon: accordionConfig.icon[statuses?.quantileCalculation as keyof typeof accordionConfig.icon],
        infoContent:
          infoContentMaps.quantileCalculation[
            statuses?.quantileCalculation as keyof typeof infoContentMaps.quantileCalculation
          ],
        infoIconColor: accordionConfig.color[statuses?.quantileCalculation ?? 'default'],
        content:
          (statuses?.quantileCalculation === 'error' || statuses?.quantileCalculation === 'success') &&
          AccordionContent({
            title:
              quantileCalculationContent[statuses?.quantileCalculation as keyof typeof quantileCalculationContent]
                ?.title,
            content:
              quantileCalculationContent[statuses?.quantileCalculation as keyof typeof quantileCalculationContent]
                ?.content,
            label:
              quantileCalculationContent[statuses?.quantileCalculation as keyof typeof quantileCalculationContent]
                ?.label,
            handler: () => handleRestartQuantileCalculation(),
          }),
      },
    ],
    [errorDescription?.validationErrorDetails, importOperationByIdQuery, onSubmitHandler, selectedValidationCase, statuses, t]
  );

  useEffect(() => {
    if (importOperationId && importOperationByIdQuery && !selectedImportOperationId) {
      dispatch(setSelectedImportOperationId(Number(importOperationId)));
    }
  }, [importOperationId, importOperationByIdQuery, selectedImportOperationId]);

  useEffect(() => {
    if (importOperationByIdQuery?.externalDto) {
      setImportOperationDetails(() => {
        const operationDetails = importOperationByIdQuery?.externalDto;
        setStatuses({
          validation: statusMap.validation[
            operationDetails?.validationStatus as keyof typeof statusMap.validation
          ] as StatusProps,
          import: statusMap.import[operationDetails?.importStatus as keyof typeof statusMap.import] as StatusProps,
          plausibility: statusMap.plausibility[
            operationDetails?.plausibilityStatus as keyof typeof statusMap.plausibility
          ] as StatusProps,
          quantileCalculation: statusMap.quantileCalculation[
            operationDetails?.quantileCalculationStatus as keyof typeof statusMap.quantileCalculation
          ] as StatusProps,
        });
        setValidationErrorReason(
          validationErrorMap[operationDetails?.validationErrorReason as keyof typeof validationErrorMap]
        );
        setErrorDescription({
          validationErrorDetails: operationDetails?.validationErrorDetails,
          importErrorDetails: operationDetails?.importErrorDetails,
        });

        return operationDetails;
      });
    }
  }, [importOperationByIdQuery]);

  return (
    <DefaultPageLayoutTsoPq>
      <StyledUploadDetailsWrapper>
        {importOperationIsLoading ? (
          <LoadingSpinnerTsoPq />
        ) : (
          <>
            <StyledUploadDetailsPageHeaderWrapper>
              <StyledUploadTitleContainer>
                <BreadcrumbsTsoPq breadcrumbs={breadcrumbs} />
                <TypographyTsoPq typographyType="pageTitleBold">
                  {importOperationDetails?.fileName ?? t('upload.noFile')}
                </TypographyTsoPq>
              </StyledUploadTitleContainer>
              <StyledImportDetailsContainer>
                <TypographyTsoPq typographyType="sectionTitleSMBold">{t('upload.detailedView.title')}</TypographyTsoPq>
                <StyledDetailColumnsContainer>
                  {pageSummaryColumns.map((column) => (
                    <StyledDetailColumn key={column.title}>
                      <TypographyTsoPq
                        typographyType="labelSM"
                        sx={{
                          color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
                        }}
                      >
                        {column.title}
                      </TypographyTsoPq>
                      <TypographyTsoPq typographyType="labelMD">{column.value}</TypographyTsoPq>
                    </StyledDetailColumn>
                  ))}
                </StyledDetailColumnsContainer>
              </StyledImportDetailsContainer>
            </StyledUploadDetailsPageHeaderWrapper>
            <StyledDetailsSection>
              {detailSectionMap.map((detailProps) => (
                <AccordionTsoPq key={detailProps.title} {...detailProps} />
              ))}
            </StyledDetailsSection>
          </>
        )}
      </StyledUploadDetailsWrapper>
    </DefaultPageLayoutTsoPq>
  );
};

export default DetailedViewUploadedFile;
